/**
 * One user's group, row restrictions and metric visibility, for a user with no Frontegg roles to
 * edit beside them: an agent (`agent:team-<id>`). The same settings and editors as a person's
 * (Users.tsx UserPermissionsDialog), saved through PUT /user-permissions/<id>.
 */
import { useState } from 'react';
import { Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useUserSettings, useUpdateUserSettings } from '@/hooks/useUserSettings';
import { useScopeCatalog, useUserGroups } from '@/hooks/useUserGroups';
import { apiErrorMessage } from '@/services/api.service';
import type { UserSettings } from '@/services/user.service';
import type { RowRestriction } from '@/services/userGroup.service';
import { toast } from 'sonner';
import { MetricVisibilityEditor, RowRestrictionsEditor, notifyRestrictionChecks } from './ScopeEditors';

export default function PrincipalPermissions({ principalId, name, onDone }: {
  principalId: string;
  name: string;
  onDone?: () => void; // after a save or cancel (a dialog closes)
}) {
  const { data: saved, isLoading } = useUserSettings(principalId);
  // Both branches, independent of Develop mode: a user's scope isn't branched.
  const { data: catalog, isLoading: catalogLoading } = useScopeCatalog();
  const { data: groups } = useUserGroups();
  const update = useUpdateUserSettings();

  const [localRestrictions, setLocalRestrictions] = useState<RowRestriction[] | null>(null);
  const [localHidden, setLocalHidden] = useState<string[] | null>(null);
  const [localGroupId, setLocalGroupId] = useState<string | null | undefined>(undefined);
  const restrictions = localRestrictions ?? saved?.row_restrictions ?? [];
  const hidden = localHidden ?? saved?.blacklisted_metric_ids ?? [];
  const groupId = localGroupId !== undefined ? localGroupId : (saved?.group_id ?? null);
  const dirty = localRestrictions !== null || localHidden !== null || localGroupId !== undefined;
  const reset = () => {
    setLocalRestrictions(null);
    setLocalHidden(null);
    setLocalGroupId(undefined);
  };

  const save = async () => {
    // Only what changed here, so a stale copy never overwrites another admin's edit.
    const settings: Partial<UserSettings> = {};
    if (localRestrictions !== null) settings.row_restrictions = restrictions;
    if (localHidden !== null) settings.blacklisted_metric_ids = hidden;
    if (localGroupId !== undefined) settings.group_id = groupId;
    try {
      const result = await update.mutateAsync({ userFronteggId: principalId, settings });
      reset();
      toast.success('Permissions saved');
      notifyRestrictionChecks(result?.restriction_checks);
      onDone?.();
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to save permissions'));
    }
  };

  if (isLoading) return <div className="py-8 text-center text-muted-foreground">Loading...</div>;
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Label htmlFor="principal-group" className="shrink-0">Group</Label>
        <Select value={groupId ?? '__none__'} onValueChange={(v) => setLocalGroupId(v === '__none__' ? null : v)}>
          <SelectTrigger id="principal-group" className="w-[300px]">
            <SelectValue placeholder="No group" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">No group</SelectItem>
            {groups?.map((g) => (
              <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-start gap-2 rounded-md border bg-muted/40 p-3 text-sm">
        <Info className="h-4 w-4 shrink-0 mt-0.5 text-muted-foreground" />
        <p>
          These apply to everything {name} reads through Pliable's semantic layer, such as its questions to Ellie, with its
          group's restrictions on top. A tool that reads data another way (for example raw SQL against the warehouse) isn't
          limited by them.
        </p>
      </div>
      <Tabs defaultValue="rows">
        <TabsList>
          <TabsTrigger value="rows">Row Restrictions</TabsTrigger>
          <TabsTrigger value="metrics">Metric Visibility</TabsTrigger>
        </TabsList>
        <TabsContent value="rows">
          <RowRestrictionsEditor
            restrictions={restrictions}
            onChange={setLocalRestrictions}
            dimensions={catalog?.dimensions ?? []}
            metrics={catalog?.metrics ?? []}
            hiddenMetricIds={hidden}
          />
        </TabsContent>
        <TabsContent value="metrics">
          <MetricVisibilityEditor metrics={catalog?.metrics ?? []} hiddenMetricIds={hidden} onChange={setLocalHidden} isLoading={catalogLoading} />
        </TabsContent>
      </Tabs>
      <div className="flex justify-end gap-2">
        {(dirty || onDone) && (
          <Button
            variant="outline"
            onClick={() => {
              reset();
              onDone?.();
            }}
          >
            Cancel
          </Button>
        )}
        <Button onClick={save} disabled={!dirty || update.isPending}>
          {update.isPending ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </div>
  );
}
